import { Button, ButtonProps, chakra } from '@chakra-ui/react'
import * as R from 'ramda'
import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import Link, { NavLink, NavLinkProps } from 'redux-first-router-link'

import { FilterEndpoint } from '../../filters/filter.reducers'
import { FilterSelector } from '../../filters/filter.selector'
import { LocationSelector } from '../../redux/selectors/location.selector'
import { Page } from '../../utils/enums'

export interface RouterLinkProps extends NavLinkProps {
  isNav?: boolean
  to:
    | string
    | Page
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | ({ [key: string]: any; type: Page } & Partial<NavLinkProps>)
}

export type RouterButtonProps = RouterLinkProps & ButtonProps

const sxLink = {
  a: { color: 'blue.60' },
  'a h1, a h3, a h3, a h4, a h5, a h6, a p': {
    color: 'deepPurple',
  },
  'a:active': { color: 'blue.70' },
  'a:hover': { color: 'blue.50' },
  'a:visited': { color: 'indigo.60' },
  'a[disabled]': { color: 'neutral.30' },
}

const useBuildRouterLink = (to: RouterLinkProps['to']) => {
  const objectTo =
    typeof to === 'object'
      ? to
      : { filters: {}, payload: {}, query: {}, type: to }
  const filters = useSelector(
    FilterSelector.byEndpoint(objectTo.type as FilterEndpoint),
  )
  const locale = useSelector(LocationSelector.locale)

  return R.mergeDeepRight(
    { payload: { ...locale, ...objectTo.payload } },
    {
      ...objectTo,
      query: { ...(objectTo.filters || {}), ...filters, ...objectTo.query },
    },
  )
}

export const RouterLink: React.FC<RouterLinkProps> = memo(
  ({ children, isNav, to, ...props }) => {
    const toLink = useBuildRouterLink(to)
    const L = chakra(isNav ? NavLink : Link)

    return (
      <L to={toLink} {...props} sx={sxLink}>
        {children}
      </L>
    )
  },
)
RouterLink.displayName = 'RouterLink'

const routerLinkPropsNames = [
  'redirect',
  'replace',
  'tagName',
  'down',
  'shouldDispatch',
  'target',
  'activeClassName',
  'activeStyle',
  'ariaCurrent',
  'exact',
  'strict',
  'isActive',
]

export const RouterButton: React.FC<RouterButtonProps> = memo(
  ({ children, isNav, to, ...props }) => {
    const toLink = useBuildRouterLink(to)
    const linkProps = R.pick(routerLinkPropsNames, props)
    const buttonProps = R.omit(routerLinkPropsNames, props)

    const L = chakra(isNav ? NavLink : Link)

    return (
      <L to={toLink} {...linkProps}>
        <Button {...buttonProps} aria-hidden="true" tabIndex={-1}>
          {children}
        </Button>
      </L>
    )
  },
)
RouterButton.displayName = 'RouterButton'
